import { useState, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Pencil, Trash2, X, Ticket, Eye } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  usePunchCardTemplates,
  useCreatePunchCardTemplate,
  useUpdatePunchCardTemplate,
  useDeletePunchCardTemplate,
  type PunchCardTemplate,
} from '@/hooks/usePunchCardTemplates';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SectionCard } from './ClinicGeneral';
import { SettingsLoading } from './settingsChrome';
import { ServiceMultiSelect } from '@/components/punchCards/ServiceMultiSelect';
import { useClinic, useUpdateClinicSettings } from '@/hooks/useClinicSettings';
import { useServicesList } from '@/hooks/useServices';
import {
  parsePunchCardServiceIds,
  toPunchCardAppointmentTypes,
} from '@/lib/punchCardEligibility';
import {
  parsePunchCardCancellationPolicy,
  type PunchCardCancelAction,
  type PunchCardCancellationPolicy,
} from '@/lib/bookingPolicies';
import type { Json } from '@/types/database.generated';
import { VatMomsField } from '@/components/ui/VatMomsField';
import { clinicVatRatePercent, itemIncludesVat, itemVatRatePercent } from '@/lib/vat';

interface TemplateFormData {
  name: string;
  description: string;
  total_clips: number;
  price_cents: number;
  practitioner_clip_value_cents: number | null;
  currency: string;
  expires_months: number | null;
  display_order: number;
  is_active: boolean;
  visible_in_portal: boolean;
  cancellation_before: PunchCardCancelAction;
  cancellation_after: PunchCardCancelAction;
  vat_rate_percent: number;
  service_ids: string[];
}

const DEFAULT_FORM: TemplateFormData = {
  name: '',
  description: '',
  total_clips: 10,
  price_cents: 0,
  practitioner_clip_value_cents: null,
  currency: 'DKK',
  expires_months: 12,
  display_order: 0,
  is_active: true,
  visible_in_portal: false,
  cancellation_before: 'forfeit',
  cancellation_after: 'forfeit',
  vat_rate_percent: 25,
  service_ids: [],
};

export function PunchCardTemplates() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();

  const canEdit = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));

  const { data: templates = [], isLoading, error } = usePunchCardTemplates(activeClinicId);
  const { data: services = [] } = useServicesList(activeClinicId, { includeInactive: true });
  const { data: clinic } = useClinic(activeClinicId);
  const clinicVatRate = clinicVatRatePercent(clinic);
  const updateClinic = useUpdateClinicSettings(activeClinicId);
  const create = useCreatePunchCardTemplate();
  const update = useUpdatePunchCardTemplate();
  const remove = useDeletePunchCardTemplate();
  const [clinicPolicy, setClinicPolicy] = useState<PunchCardCancellationPolicy>({
    before_deadline: { action: 'forfeit' },
    after_deadline: { action: 'forfeit' },
  });

  const [editing, setEditing] = useState<PunchCardTemplate | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<TemplateFormData>(DEFAULT_FORM);
  const [priceDisplay, setPriceDisplay] = useState('');

  const serviceOptions = services
    .filter((service) => !service.is_blocker && (service.is_active || form.service_ids.includes(service.id)))
    .map((service) => ({ id: service.id, name: service.name }));

  useEffect(() => {
    if (!clinic) return;
    setClinicPolicy(
      parsePunchCardCancellationPolicy(
        (clinic as { punch_card_cancellation_policy?: unknown }).punch_card_cancellation_policy,
      ),
    );
  }, [clinic]);

  function openCreate() {
    setEditing(null);
    setForm({ ...DEFAULT_FORM, vat_rate_percent: clinicVatRate });
    setPriceDisplay('');
    setShowForm(true);
  }

  function openEdit(template: PunchCardTemplate) {
    setEditing(template);
    const parsed = parsePunchCardCancellationPolicy(template.cancellation_policy);
    setForm({
      name: template.name,
      description: template.description ?? '',
      total_clips: template.total_clips,
      price_cents: template.price_cents,
      practitioner_clip_value_cents: template.practitioner_clip_value_cents ?? null,
      currency: template.currency,
      expires_months: template.expires_months,
      display_order: template.display_order,
      is_active: template.is_active,
      visible_in_portal: template.visible_in_portal,
      cancellation_before: parsed.before_deadline.action,
      cancellation_after: parsed.after_deadline.action,
      vat_rate_percent: template.vat_rate_percent ?? clinicVatRate,
      service_ids: parsePunchCardServiceIds(template.appointment_types),
    });
    setPriceDisplay((template.price_cents / 100).toFixed(2));
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditing(null);
    setForm(DEFAULT_FORM);
    setPriceDisplay('');
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!activeClinicId) return;

    const parsedPrice = parseFloat(priceDisplay.replace(',', '.'));
    if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
      toast.error(t('patients.punch_cards.price_required', 'Please enter a valid price'));
      return;
    }
    const price_cents = Math.round(parsedPrice * 100);

    const cancellation_policy = {
      before_deadline: { action: form.cancellation_before },
      after_deadline: { action: form.cancellation_after },
    };
    const {
      cancellation_before: _before,
      cancellation_after: _after,
      vat_rate_percent: _vat,
      service_ids,
      ...templateFields
    } = form;
    const vat_rate_percent = itemVatRatePercent(
      itemIncludesVat(form.vat_rate_percent),
      clinicVatRate,
    );
    const appointment_types = toPunchCardAppointmentTypes(service_ids);

    try {
      if (editing) {
        await update.mutateAsync({
          id: editing.id,
          ...templateFields,
          appointment_types,
          price_cents,
          vat_rate_percent,
          cancellation_policy,
        });
        toast.success(t('patients.punch_cards.template_updated', 'Template updated'));
      } else {
        await create.mutateAsync({
          clinic_id: activeClinicId,
          appointment_types,
          group_types: [],
          ...templateFields,
          price_cents,
          vat_rate_percent,
          cancellation_policy,
        });
        toast.success(t('patients.punch_cards.template_created', 'Template created'));
      }
      closeForm();
    } catch (err) {
      toast.error(
        editing
          ? t('patients.punch_cards.template_update_error', 'Failed to update template')
          : t('patients.punch_cards.template_create_error', 'Failed to create template'),
      );
    }
  }

  async function handleDelete(template: PunchCardTemplate) {
    if (!activeClinicId) return;
    if (
      !window.confirm(
        t('patients.punch_cards.confirm_delete_template', `Delete template "${template.name}"?`),
      )
    ) {
      return;
    }

    try {
      await remove.mutateAsync({ id: template.id, clinicId: activeClinicId });
      toast.success(t('patients.punch_cards.template_deleted', 'Template deleted'));
    } catch (err) {
      toast.error(t('patients.punch_cards.template_delete_error', 'Failed to delete template'));
    }
  }

  if (isLoading) {
    return <SettingsLoading />;
  }

  if (error) {
    return (
      <ErrorAlert
        message={t('patients.punch_cards.templates_load_error', 'Failed to load templates')}
      />
    );
  }

  return (
    <div className="space-y-6">
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await updateClinic.mutateAsync({
              punch_card_cancellation_policy: clinicPolicy as unknown as Json,
            });
            toast.success(t('settings.saved_toast'));
          } catch {
            toast.error(t('settings.save_error_toast'));
          }
        }}
      >
        <SectionCard
          title={t('booking_policies.punch_card_policy_title')}
          subtitle={t('booking_policies.punch_card_policy_subtitle')}
          readOnly={!canEdit}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            {(['before_deadline', 'after_deadline'] as const).map((side) => (
              <div key={side}>
                <label className="text-sm font-medium text-ds-text">
                  {t(`booking_policies.${side}`)}
                </label>
                <select
                  value={clinicPolicy[side].action}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setClinicPolicy((prev) => ({
                      ...prev,
                      [side]: { action: e.target.value as PunchCardCancelAction },
                    }))
                  }
                  className="mt-1 h-11 w-full rounded-xl border border-ds-field bg-ds-card px-3 text-sm text-ds-text"
                >
                  <option value="forfeit">{t('booking_policies.punch_forfeit')}</option>
                  <option value="restore">{t('booking_policies.punch_restore')}</option>
                </select>
              </div>
            ))}
          </div>
          {canEdit && (
            <div className="mt-4 flex justify-end">
              <Button type="submit" disabled={updateClinic.isPending}>
                {updateClinic.isPending ? t('settings.saving') : t('settings.save')}
              </Button>
            </div>
          )}
        </SectionCard>
      </form>

      <SectionCard
        title={t('patients.punch_cards.templates', 'Punch Card Templates')}
        subtitle={t(
          'patients.punch_cards.templates_description',
          'Create reusable templates for quick punch card creation. Templates can be used by staff or made available for patients to purchase online.',
        )}
      >
        <div className="space-y-4">
          {canEdit && (
            <Button onClick={openCreate} variant="primary" disabled={showForm}>
              <Plus className="mr-2 h-4 w-4" />
              {t('patients.punch_cards.new_template', 'New Template')}
            </Button>
          )}

          {showForm && (
            <form
              onSubmit={onSubmit}
              className="space-y-4 rounded-lg border border-ds-line bg-ds-sunk p-4"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-ds-text">
                  {editing
                    ? t('patients.punch_cards.edit_template', 'Edit Template')
                    : t('patients.punch_cards.new_template', 'New Template')}
                </h3>
                <Button type="button" variant="tertiary" size="icon" onClick={closeForm} aria-label={t('common.close')}>
                  <X />
                </Button>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Input
                    label={t('patients.punch_cards.name', 'Name')}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t(
                      'patients.punch_cards.name_placeholder',
                      'e.g., 10 Massage Sessions',
                    )}
                    required
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-ds-text">
                    {t('patients.punch_cards.description', 'Description')}
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full rounded-md border border-ds-field px-3 py-2 shadow-card focus:border-ds-teal focus:outline-none focus:shadow-ring"
                    rows={2}
                    placeholder={t(
                      'patients.punch_cards.description_placeholder',
                      'Optional description',
                    )}
                  />
                </div>

                <Input
                  type="number"
                  label={t('patients.punch_cards.total_clips', 'Total Clips')}
                  value={form.total_clips}
                  onChange={(e) => setForm({ ...form, total_clips: parseInt(e.target.value) || 0 })}
                  min={1}
                  max={100}
                  required
                />

                <details className="sm:col-span-2">
                  <summary className="cursor-pointer text-sm text-ds-muted">
                    {t('appointments.override.more', 'Flere muligheder')}
                  </summary>
                  <div className="mt-3">
                    <Input
                      type="text"
                      inputMode="decimal"
                      label={t(
                        'patients.punch_cards.staff_value',
                        'Behandlerens værdi pr. klip (valgfri)',
                      )}
                      hint={t(
                        'patients.punch_cards.staff_value_hint',
                        'Tom = kortets pris delt med antal klip',
                      )}
                      value={
                        form.practitioner_clip_value_cents == null
                          ? ''
                          : (form.practitioner_clip_value_cents / 100).toFixed(2)
                      }
                      onChange={(e) => {
                        const raw = e.target.value.trim();
                        setForm({
                          ...form,
                          practitioner_clip_value_cents: raw
                            ? Math.round(Number(raw.replace(',', '.')) * 100)
                            : null,
                        });
                      }}
                    />
                  </div>
                </details>
                <Input
                  type="text"
                  inputMode="decimal"
                  label={t('patients.punch_cards.price_dkk', 'Price (DKK)')}
                  value={priceDisplay}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '' || /^\d*[.,]?\d{0,2}$/.test(raw)) {
                      setPriceDisplay(raw);
                    }
                  }}
                  placeholder="0.00"
                  required
                />

                <div className="sm:col-span-2">
                  <VatMomsField
                    includesVat={itemIncludesVat(form.vat_rate_percent)}
                    clinicRatePercent={clinicVatRate}
                    onChange={(includesVat) =>
                      setForm({
                        ...form,
                        vat_rate_percent: itemVatRatePercent(includesVat, clinicVatRate),
                      })
                    }
                  />
                </div>

                <Input
                  type="number"
                  label={t('patients.punch_cards.expires_months', 'Valid for (months)')}
                  value={form.expires_months || ''}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      expires_months: e.target.value ? parseInt(e.target.value) : null,
                    })
                  }
                  min={1}
                  placeholder={t('patients.punch_cards.no_expiry', 'No expiry')}
                />

                <Input
                  type="number"
                  label={t('patients.punch_cards.display_order', 'Display Order')}
                  value={form.display_order}
                  onChange={(e) =>
                    setForm({ ...form, display_order: parseInt(e.target.value) || 0 })
                  }
                  min={0}
                />

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-ds-text">
                    {t('patients.punch_cards.included_services', 'Ydelser inkluderet')}
                  </label>
                  <p className="mb-2 text-xs text-ds-muted">
                    {t(
                      'patients.punch_cards.included_services_hint',
                      'Tom = alle ydelser. Ellers kan klippet kun bruges på de valgte ydelser.',
                    )}
                  </p>
                  <ServiceMultiSelect
                    options={serviceOptions}
                    selectedIds={form.service_ids}
                    onChange={(service_ids) => setForm({ ...form, service_ids })}
                    disabled={!canEdit}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="h-4 w-4 rounded border-ds-field text-ds-teal focus:shadow-ring"
                  />
                  <span className="text-sm text-ds-text">
                    {t('patients.punch_cards.is_active', 'Active')}
                  </span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.visible_in_portal}
                    onChange={(e) => setForm({ ...form, visible_in_portal: e.target.checked })}
                    className="h-4 w-4 rounded border-ds-field text-ds-teal focus:shadow-ring"
                  />
                  <span className="text-sm text-ds-text">
                    {t(
                      'patients.punch_cards.visible_in_portal',
                      'Allow patients to purchase online',
                    )}
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-ds-text">
                    {t('booking_policies.template_cancel_before')}
                  </label>
                  <select
                    value={form.cancellation_before}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        cancellation_before: e.target.value as PunchCardCancelAction,
                      })
                    }
                    className="mt-1 h-11 w-full rounded-xl border border-ds-field bg-ds-card px-3 text-sm text-ds-text"
                  >
                    <option value="forfeit">{t('booking_policies.punch_forfeit')}</option>
                    <option value="restore">{t('booking_policies.punch_restore')}</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-ds-text">
                    {t('booking_policies.template_cancel_after')}
                  </label>
                  <select
                    value={form.cancellation_after}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        cancellation_after: e.target.value as PunchCardCancelAction,
                      })
                    }
                    className="mt-1 h-11 w-full rounded-xl border border-ds-field bg-ds-card px-3 text-sm text-ds-text"
                  >
                    <option value="forfeit">{t('booking_policies.punch_forfeit')}</option>
                    <option value="restore">{t('booking_policies.punch_restore')}</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="secondary" onClick={closeForm}>
                  {t('common.cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={create.isPending || update.isPending}
                >
                  {create.isPending || update.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : editing ? (
                    t('common.save', 'Save')
                  ) : (
                    t('common.create', 'Create')
                  )}
                </Button>
              </div>
            </form>
          )}

          {templates.length === 0 ? (
            <div className="rounded-lg border border-dashed border-ds-line p-12 text-center">
              <Ticket className="mx-auto h-12 w-12 text-ds-muted" />
              <h3 className="mt-2 text-sm font-medium text-ds-text">
                {t('patients.punch_cards.no_templates', 'No templates yet')}
              </h3>
              <p className="mt-1 text-sm text-ds-muted">
                {t(
                  'patients.punch_cards.no_templates_hint',
                  'Create your first template to get started',
                )}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="flex items-center justify-between rounded-lg border border-ds-line bg-ds-card p-4"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Ticket className="h-5 w-5 text-ds-teal" />
                      <h4 className="font-medium text-ds-text">{template.name}</h4>
                      {!template.is_active && (
                        <span className="rounded-full bg-ds-neutral-tint px-2 py-0.5 text-xs text-ds-muted">
                          {t('common.inactive', 'Inactive')}
                        </span>
                      )}
                      {template.visible_in_portal && (
                        <span className="flex items-center gap-1 rounded-full bg-ds-green-tint px-2 py-0.5 text-xs text-ds-green-ink">
                          <Eye className="h-3 w-3" />
                          {t('patients.punch_cards.online', 'Online')}
                        </span>
                      )}
                    </div>
                    {template.description && (
                      <p className="mt-1 text-sm text-ds-muted">
                        {template.description}
                      </p>
                    )}
                    <div className="mt-2 flex items-center gap-4 text-sm text-ds-muted">
                      <span>
                        {template.total_clips} {t('patients.punch_cards.clips', 'clips')}
                      </span>
                      <span>
                        {(template.price_cents / 100).toFixed(0)} {template.currency}
                      </span>
                      <span>
                        {itemIncludesVat(template.vat_rate_percent)
                          ? t('vat.with_vat')
                          : t('vat.without_vat')}
                      </span>
                      {template.expires_months && (
                        <span>
                          {t('patients.punch_cards.valid_for', 'Valid for')}{' '}
                          {template.expires_months} {t('common.months', 'months')}
                        </span>
                      )}
                      <span>
                        {(() => {
                          const ids = parsePunchCardServiceIds(template.appointment_types);
                          if (ids.length === 0) {
                            return t('patients.punch_cards.services_all', 'Alle ydelser');
                          }
                          const names = ids
                            .map((id) => services.find((service) => service.id === id)?.name)
                            .filter((name): name is string => Boolean(name));
                          return names.length > 0
                            ? names.join(', ')
                            : t('patients.punch_cards.services_selected_count', {
                                count: ids.length,
                                defaultValue: '{{count}} ydelser valgt',
                              });
                        })()}
                      </span>
                    </div>
                  </div>

                  {canEdit && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openEdit(template)}
                        disabled={showForm}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(template)}
                        disabled={remove.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
